import { Box } from '@mui/material';
import { useEffect, useRef, useState, useMemo, useCallback, MouseEventHandler } from 'react';
import { FormattedMessage } from 'react-intl';
import { BaseSelect } from './BaseSelect';
import { LoadingSpinner } from './LoadingSpinner';
import { TbIcon } from 'components/Figma';
import { Button } from 'components/Button';
import { SelectOption } from 'components/select/types';

export interface BaseAsyncSelectProps<T> {
  selectedItems: T[] | null;
  onSelectionChange: (items: T[] | null) => void;
  fetchItems: (searchTerm: string) => Promise<T[] | null | undefined>;
  getItemId: (item: T) => string;
  itemToOption: (item: T) => SelectOption<string>;
  minSearchCharacters?: number;
  placeholder: string;
  label?: string;
  id: string;
  showConfirmButton?: boolean;
  className?: string;
  disabled?: boolean;
  renderMenuBottomExtra?: (
    searchValue: string,
    minSearchCharacters: number,
    resultsCount: number,
    onClose?: MouseEventHandler
  ) => React.ReactNode;
}

const customFilterFunc = () => true;

export function BaseAsyncSelect<T>({
  selectedItems,
  onSelectionChange,
  fetchItems,
  getItemId,
  itemToOption,
  minSearchCharacters = 3,
  placeholder,
  label = undefined,
  id,
  showConfirmButton = false,
  className = '',
  renderMenuBottomExtra = undefined,
  disabled = false,
}: BaseAsyncSelectProps<T>) {
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<T[]>([]);
  const lastSearchLength = useRef(0);
  const [itemMap, setItemMap] = useState<Map<string, T>>(new Map());
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const onCloseRef = useRef<MouseEventHandler | null>(null);
  const selectedIdsAtOpenRef = useRef<Set<string>>(new Set());

  // Stabilize prop functions to prevent unnecessary re-renders
  const stableGetItemId = useCallback(getItemId, [getItemId]);
  const stableItemToOption = useCallback(itemToOption, [itemToOption]);

  useEffect(() => {
    let active = true;

    if (searchValue.length < minSearchCharacters) {
      if (lastSearchLength.current >= minSearchCharacters || searchValue.length === 0) {
        setSearchResults([]);
        setIsLoading(false);
      }
    }

    // Only fetch when menu is open
    if (!isOpen) {
      lastSearchLength.current = searchValue.length;
      return;
    }

    if (searchValue.length >= minSearchCharacters) {
      setIsLoading(true);
      (async () => {
        const items = await fetchItems(searchValue);
        if (!active) {
          return;
        }
        const fetchedItems = items ?? [];
        setSearchResults(fetchedItems);
        setIsLoading(false);

        setItemMap(prev => {
          const newMap = new Map(prev);
          fetchedItems.forEach(item => newMap.set(stableGetItemId(item), item));
          return newMap;
        });
      })();
    }

    lastSearchLength.current = searchValue.length;

    return () => {
      active = false;
    };
  }, [searchValue, isOpen, fetchItems, stableGetItemId, minSearchCharacters]);

  useEffect(() => {
    if (selectedItems) {
      setItemMap(prev => {
        const newMap = new Map(prev);
        selectedItems.forEach(item => newMap.set(stableGetItemId(item), item));
        return newMap;
      });
    }
  }, [selectedItems, stableGetItemId]);

  // Memoize selectedIds - only recompute when selectedItems changes
  const selectedIds = useMemo(() => (selectedItems ?? []).map(stableGetItemId), [selectedItems, stableGetItemId]);

  const searchResultIds = useMemo(() => searchResults.map(stableGetItemId), [searchResults, stableGetItemId]);

  // Memoize allItems with optimized O(n) deduplication using Set
  const allItems = useMemo(() => {
    const seen = new Set<string>();
    const result: T[] = [];

    // First, add items that were selected when dropdown opened (these go to top)
    selectedIdsAtOpenRef.current.forEach(id => {
      const item = itemMap.get(id);
      if (item) {
        seen.add(id);
        result.push(item);
      }
    });

    // Then add search results (including newly selected items in their original position)
    searchResults.forEach(item => {
      const id = stableGetItemId(item);
      if (!seen.has(id)) {
        seen.add(id);
        result.push(item);
      }
    });

    // Finally, add any other selected items not yet included
    (selectedItems ?? []).forEach(item => {
      const id = stableGetItemId(item);
      if (!seen.has(id)) {
        seen.add(id);
        result.push(item);
      }
    });

    return result;
  }, [selectedItems, searchResults, stableGetItemId]);

  const allOptions = useMemo(() => allItems.map(stableItemToOption), [allItems, stableItemToOption]);

  // Memoize boolean flags for button state
  const hasSearchResults = useMemo(() => searchResults.length > 0, [searchResults.length]);
  const allSearchResultsSelected = useMemo(
    () => hasSearchResults && searchResultIds.every(id => selectedIds.includes(id)),
    [hasSearchResults, searchResultIds, selectedIds]
  );

  const handleFilter = useCallback((_filteredCount: number, searchTerm?: string) => {
    if (searchTerm !== undefined) {
      setSearchValue(searchTerm);
    }
  }, []);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    setSearchResults([]);
    setSearchValue('');
    setIsLoading(false);
    // Capture which items are selected when dropdown opens
    selectedIdsAtOpenRef.current = new Set((selectedItems ?? []).map(stableGetItemId));
  }, [selectedItems, stableGetItemId]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleSelectionChange = useCallback(
    (selectedIds: string[]) => {
      const items = selectedIds.map(id => itemMap.get(id)).filter((item): item is T => Boolean(item));
      onSelectionChange(items.length > 0 ? items : null);
    },
    [itemMap, onSelectionChange]
  );

  const handleSelectAllClick = useCallback(() => {
    if (allSearchResultsSelected || (selectedItems && !hasSearchResults)) {
      onSelectionChange(null);
    } else if (hasSearchResults) {
      // Select all from search results while avoiding duplicates
      const seen = new Set((selectedItems ?? []).map(stableGetItemId));
      const newItems = [...(selectedItems ?? [])];

      searchResults.forEach(item => {
        const id = stableGetItemId(item);
        if (!seen.has(id)) {
          newItems.push(item);
        }
      });

      onSelectionChange(newItems);
    }
  }, [
    allSearchResultsSelected,
    selectedItems,
    hasSearchResults,
    onSelectionChange,
    selectedItems,
    searchResults,
    stableGetItemId,
  ]);

  const handleConfirmClick = useCallback((event: React.MouseEvent) => {
    setIsOpen(false);
    if (onCloseRef.current) {
      onCloseRef.current(event);
    } else {
      document.body.click();
    }
  }, []);

  const customRenderMenuBottomExtra = useCallback(
    (_searchValue: string, _minSearchCharacters: number, _resultsCount: number, onClose: MouseEventHandler) => {
      onCloseRef.current = onClose;

      const isDisabled = !hasSearchResults && !selectedItems;
      const buttonText =
        allSearchResultsSelected || (selectedItems && !hasSearchResults) ? (
          <FormattedMessage id="select.clear-all" defaultMessage="Clear All" />
        ) : (
          <FormattedMessage id="select.select-all" defaultMessage="Select All" />
        );

      const shouldShowEmptyState = searchValue.length < minSearchCharacters && searchResults.length === 0;
      const shouldShowNoResults = searchValue.length >= minSearchCharacters && searchResults.length === 0 && !isLoading;

      return (
        <Box className="w-full flex flex-col gap-1">
          {/* Empty state / No results message - styled to look like it's in the list */}
          {(shouldShowEmptyState || shouldShowNoResults) && (
            <div className="flex items-center gap-x-2 text-xss text-text-disclaimer">
              <TbIcon name="InformationOutlined" />
              <p className="leading-none">
                {shouldShowEmptyState ? (
                  minSearchCharacters === 1 ? (
                    <FormattedMessage
                      id="async-select.search.empty_state.singular"
                      defaultMessage="Start search by entering at least one character."
                    />
                  ) : (
                    <FormattedMessage
                      id="async-select.search.empty_state.plural"
                      defaultMessage="Start search by entering at least {min_chars} characters."
                      values={{ min_chars: minSearchCharacters }}
                    />
                  )
                ) : shouldShowNoResults ? (
                  <FormattedMessage id="async-select.search.no_results" defaultMessage="No matching results found." />
                ) : null}
              </p>
            </div>
          )}

          {/* Buttons and consumer's custom content */}
          <Box className="flex flex-col justify-between items-center ">
            <Box className="flex flex-col">
              {!isLoading && renderMenuBottomExtra && (
                <Box className="pt-1">
                  {renderMenuBottomExtra(searchValue, minSearchCharacters, searchResults.length, onClose)}
                </Box>
              )}
            </Box>
            <div className="flex flex-row justify-between w-full pt-3">
              <div>{isLoading ? <LoadingSpinner size="small" /> : ''}</div>
              <div className="flex gap-2">
                <Button size="sm" priority="secondary" onClick={handleSelectAllClick} disabled={isDisabled}>
                  {buttonText}
                </Button>
                {showConfirmButton && (
                  <Button size="sm" onClick={handleConfirmClick}>
                    <FormattedMessage id="select.confirm" defaultMessage="Confirm" />
                  </Button>
                )}
              </div>
            </div>
          </Box>
        </Box>
      );
    },
    [
      hasSearchResults,
      selectedItems,
      allSearchResultsSelected,
      isLoading,
      renderMenuBottomExtra,
      searchValue,
      minSearchCharacters,
      searchResults.length,
      handleSelectAllClick,
      handleConfirmClick,
      showConfirmButton,
    ]
  );

  return (
    <BaseSelect
      id={id}
      disabled={disabled}
      label={label ?? placeholder}
      selectedItems={selectedIds}
      allOptionItems={allOptions}
      setSelectedItems={handleSelectionChange}
      onFilter={handleFilter}
      onOpen={handleOpen}
      onClose={handleClose}
      searchPlaceholder={placeholder}
      filterFunc={customFilterFunc}
      showConfirmButton={false}
      className={className}
      renderMenuBottomExtra={customRenderMenuBottomExtra}
      minSearchCharacters={minSearchCharacters}
      hideDefaultButtons={true}
    />
  );
}
